import { BigNumber } from 'bignumber.js';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { type ChainAddressId } from '@infinex/asset-config';
import { getPreferredGasAccountAssets } from '@infinex/gas-account';
import {
  type GasDepositQuoteOutput,
  MAXIMUM_GAS_ACCOUNT_BALANCE,
  MINIMUM_GAS_ACCOUNT_TOPUP,
} from '@infinex/main-service-contract/v2/client/gas';

import { useGasBalance } from '../../api/gas/useGasBalance';
import { useGasDepositQuote } from '../../api/gas/useGasDepositQuote';
import { useCustodySelection } from '../../components/wallet-selector/WalletSelectedProvider';
import { useUnifiedBalances } from '../../features/balances';
import type {
  BalancesByChainAddressId,
  ChainAssetBalanceSummed,
} from '../../features/balances';
import { calculateAssetDecimalPrecisionForUSD } from '../../utils/amount';
import type { CurrencyUnit } from '../unit-switch';

type GasDepositQuoteContextType = {
  assetSelection: {
    userInputAmount: number | undefined;
    setUserInputAmount: (amount?: number) => void;
    setUsdAmount: (amount?: number) => void;
    increaseUsdAmount: (amount: number) => void;
    handleMaxAmount: () => void;

    availableAssets: BalancesByChainAddressId;

    selectedAssetDetails: ChainAssetBalanceSummed | undefined;
    setSelectedChainId: (chainId: ChainAddressId) => void;
  };

  updateUnit: (unit: CurrencyUnit) => void;
  gasQuote?: GasDepositQuoteOutput;
  refetchGasQuote: () => void;
};

export const GasDepositQuoteContext = createContext<
  GasDepositQuoteContextType | undefined
>(undefined);

export const GasDepositQuoteProvider = ({
  children,
  topUpType,
}: {
  children: React.ReactNode;
  topUpType: 'prepaid_top_up' | 'jit_top_up';
}) => {
  const { selected } = useCustodySelection();
  const { data: gasBalance } = useGasBalance();

  const [userInputAmount, setUserInputAmount] = useState<number | undefined>(
    undefined
  );

  const [shouldUseMaxAmount, setShouldUseMaxAmount] = useState(false);

  const [selectedChainId, setSelectedChainId] = useState<
    ChainAddressId | undefined
  >();

  const custody = selected?.custody ?? 'wallet';

  const { balancesByChainAddressId: availableAssets = {} } = useUnifiedBalances(
    {
      custody: [custody],
      // Only pass walletIds for wallet custody, not for vaults
      walletIds:
        custody !== 'vault' && selected?.id ? [selected.id] : undefined,
    }
  );

  const preferredAssets = useMemo(
    () =>
      getPreferredGasAccountAssets(
        Object.values(availableAssets) as ChainAssetBalanceSummed[]
      ),
    [availableAssets]
  );

  const selectedAssetDetails = useMemo(
    () =>
      selectedChainId
        ? preferredAssets.find((a) => a.id === selectedChainId)
        : undefined,
    [preferredAssets, selectedChainId]
  );

  // we can't read from useCurrencyUnit so we need to store the unit here, and it'll be synced from the CurrencyUnitProvider
  // see AddFundsScreen
  const [unit, setUnit] = useState<CurrencyUnit>('usd');

  let currentGasAmountInUsd = 0;
  if (unit === 'usd') {
    currentGasAmountInUsd = userInputAmount ?? 0;
  } else {
    currentGasAmountInUsd = BigNumber(userInputAmount ?? 0)
      .multipliedBy(selectedAssetDetails?.rateUsd ?? 0)
      .toNumber();
  }

  const { data: gasQuote, refetch } = useGasDepositQuote({
    gasAmount: currentGasAmountInUsd,
    userWalletId: custody === 'vault' ? undefined : selected?.id,
    custody,
    chain: selectedAssetDetails?.chain,
    assetAddress: selectedAssetDetails?.assetAddress,
    topUpType,
    isMaxTopup: shouldUseMaxAmount,
  });

  useEffect(() => {
    if (
      selectedChainId &&
      preferredAssets.find((a) => a.id === selectedChainId)
    ) {
      return;
    }
    const firstChainId = preferredAssets.find(
      (asset) => (asset?.balanceUsd ?? 0) >= MINIMUM_GAS_ACCOUNT_TOPUP
    );
    if (firstChainId) {
      setSelectedChainId(firstChainId.id);
    }
  }, [preferredAssets, selectedChainId]);

  const handleSetUserInputAmount = (amount?: number) => {
    setShouldUseMaxAmount(false);
    setUserInputAmount(amount);
  };

  const handleSetUsdAmount = (amount?: number) => {
    setShouldUseMaxAmount(false);

    if (!amount) {
      setUserInputAmount(undefined);
      return;
    }

    if (unit === 'usd') {
      setUserInputAmount(amount);
    } else {
      const tokenAmount = calculateTokenAmountFromUsdAmount(
        amount,
        selectedAssetDetails?.rateUsd,
        BigNumber.ROUND_UP
      );
      if (tokenAmount === null) {
        return;
      }
      setUserInputAmount(tokenAmount.toNumber());
    }
  };

  const handleIncreaseUsdAmount = (amount: number) => {
    setShouldUseMaxAmount(false);
    if (unit === 'usd') {
      setUserInputAmount((prev) =>
        BigNumber(prev ?? 0)
          .plus(amount)
          .decimalPlaces(2, BigNumber.ROUND_HALF_UP)
          .toNumber()
      );
    } else {
      const tokenAmountToInCrease = calculateTokenAmountFromUsdAmount(
        amount,
        selectedAssetDetails?.rateUsd,
        BigNumber.ROUND_UP
      );

      if (tokenAmountToInCrease === null || !selectedAssetDetails?.rateUsd) {
        return;
      }

      setUserInputAmount((prev) => {
        return BigNumber(prev ?? 0)
          .plus(tokenAmountToInCrease)
          .decimalPlaces(
            calculateAssetDecimalPrecisionForUSD(selectedAssetDetails.rateUsd!)
          )
          .toNumber();
      });
    }
  };

  const handleMaxAmount = () => {
    if (!selectedAssetDetails?.balanceUsd) return;
    const remainingAllowance = MAXIMUM_GAS_ACCOUNT_BALANCE - gasBalance;
    if (remainingAllowance <= 0) return;

    setShouldUseMaxAmount(true);

    const maxAmount = Math.min(
      remainingAllowance,
      selectedAssetDetails?.balanceUsd
    );

    if (unit === 'usd') {
      setUserInputAmount(maxAmount);
    } else {
      const tokenAmount = calculateTokenAmountFromUsdAmount(
        maxAmount,
        selectedAssetDetails?.rateUsd,
        BigNumber.ROUND_DOWN // use round down to ensure we don't exceed the user's balance
      );

      if (tokenAmount === null) {
        return;
      }

      setUserInputAmount(tokenAmount.toNumber());
    }
  };

  const refetchGasQuote = () => {
    void refetch();
  };

  return (
    <GasDepositQuoteContext.Provider
      value={{
        assetSelection: {
          userInputAmount: userInputAmount,
          setUserInputAmount: handleSetUserInputAmount,
          setUsdAmount: handleSetUsdAmount,
          increaseUsdAmount: handleIncreaseUsdAmount,
          handleMaxAmount,
          availableAssets, // all available assets, (including unsupported) - UI will render them as disabled
          selectedAssetDetails,
          setSelectedChainId,
        },
        gasQuote,
        refetchGasQuote,
        updateUnit: setUnit,
      }}
    >
      {children}
    </GasDepositQuoteContext.Provider>
  );
};

export function useGasDeposit() {
  const context = useContext(GasDepositQuoteContext);
  if (!context)
    throw new Error(
      'useGasDeposit must be used within a GasDepositQuoteContext'
    );
  return context;
}

function calculateTokenAmountFromUsdAmount(
  usdAmount: number,
  rateUsd: number | null | undefined,
  roundingMode: BigNumber.RoundingMode
) {
  if (!rateUsd) {
    return null;
  }

  return BigNumber(usdAmount)
    .dividedBy(rateUsd)
    .decimalPlaces(calculateAssetDecimalPrecisionForUSD(rateUsd), roundingMode);
}
